import React, { useMemo } from 'react'
import { BookOpenText } from '@phosphor-icons/react'
import { Icon } from '@shopflo/ui'
import { useLocale } from 'src/lib/hooks/useLocal'

export interface Report {
  id: string
  title: string
  description: string
}

export interface ReportCategory {
  category: string
  reports: Report[]
}

export const reportCategories: ReportCategory[] = [
  {
    category: 'Order',
    reports: [
      {
        id: 'orders',
        title: 'Order History',
        description: 'Complete list of all orders with key details.'
      },
      {
        id: 'order_discount_report',
        title: 'Discounted Orders',
        description: 'Order-level view of discount usage with transaction details.'
      },
      {
        id: 'order_line_items',
        title: 'Product-level Orders',
        description: 'Each row represents a product line deal for SKU sales and fulfillment track'
      }
    ]
  },
  {
    category: 'Discounts',
    reports: [
      {
        id: 'discount_orders',
        title: 'Aggregated Discounts',
        description: 'Summary of all discount applications within the selected date range (X-Y).'
      },
      {
        id: 'discount_performance',
        title: 'Discount Performance',
        description: 'Performance metrics and analytics for discount campaigns.'
      },
      {
        id: 'discount_report',
        title: 'Discount Report',
        description: 'Detailed report of all discounts applied within the selected date range.'
      }
    ]
  },
  {
    category: 'Conversion',
    reports: [
      { 
        id: 'conversion_funnel_report',
        title: 'Conversion Funnel',
        description: 'Daily funnel metrics from sessions to orders with key indicators.'
      },
      // {
      //   id: 'conversion_hourly',
      //   title: 'Hourly Conversion(Single Day)',
      //   description: 'Hour-by-hour funnel performance for a selected day.'
      // },
      {
        id: 'abandoned_checkouts',
        title: 'Abandoned Checkouts',
        description: 'List of incomplete checkouts for recovery insights.'
      }
    ]
  },
  {
    category: 'Upsell',
    reports: [
      {
        id: 'upsell_orders',
        title: 'Upsell Orders',
        description: 'Order level view of upsold items'
      }
    ]
  },
  {
    category: 'Checkouts Section',
    reports: [
      {
        id: 'utm_report',
        title: 'UTM reports',
        description: 'Checkout funnel split by utm parmas'
      }
    ]
  }
]

interface ReportCategoriesProps {
  searchQuery: string
  onReportSelect: (reportId: string) => void
}

const ReportCategories: React.FC<ReportCategoriesProps> = ({ searchQuery, onReportSelect }) => {
  const { t } = useLocale()
  const filteredReports = useMemo(
    () =>
      !searchQuery
        ? reportCategories
        : reportCategories
            .map(category => ({
              ...category,
              reports: category.reports.filter(
                report =>
                  report?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  report.description?.toLowerCase().includes(searchQuery.toLowerCase())
              )
            }))
            .filter(category => category.reports.length),
    [searchQuery]
  )

  return (
    <div className='overflow-y-auto max-h-80'>
      {filteredReports.map((category, categoryIndex) => (
        <div key={categoryIndex}>
          {categoryIndex > 0 && (
            <div className='flex items-center justify-center h-4'>
              <div className='w-full h-px border-t-2 border-dotted border-zinc-300/50'></div>
            </div>
          )}
          <h3 className='flex mx-1.5 text-text-xs-medium h-8 px-2 items-center text-zinc-500'>{category.category}</h3>
          <div className='space-y-0'>
            {category.reports.map(report => {
              return (
                <div
                  key={report.id}
                  className='flex flex-1 items-start pl-2 pr-1.5 py-2 gap-2 mx-1.5 min-w-0 hover:bg-gray-50 cursor-pointer transition-colors rounded'
                  onClick={() => onReportSelect(report.id)}
                >
                  <div className='flex items-center justify-center w-5 h-5 text-zinc-500'>
                    <Icon IconComponent={BookOpenText} size='sm' className='flex w-fit' />
                  </div>
                  <div className='flex-1 min-w-0'>
                    <h4 className='text-text-sm-regular text-zinc-600'>{report.title}</h4>
                    <p className='text-text-xs-regular text-zinc-500 line-clamp-2'>{report.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {filteredReports.length === 0 && searchQuery && (
        <div className='py-4 text-sm text-center text-gray-500'>
          {t('no_reports_found')} "{searchQuery}"
        </div>
      )}
    </div>
  )
}

export default ReportCategories
